
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import ImageUpload from '@/components/shared/ImageUpload'

interface TechStackItem {
  id: string
  category: string
  technologies: string[]
  image_url: string | null
  display_order: number
  is_active: boolean
}

interface TechStackFormProps {
  techStackItem?: TechStackItem | null
  onSave: () => void
  onCancel: () => void
}

const TechStackForm = ({ techStackItem, onSave, onCancel }: TechStackFormProps) => {
  const [formData, setFormData] = useState({
    category: '',
    technologies: '',
    image_url: '',
    display_order: 0,
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (techStackItem) {
      setFormData({
        category: techStackItem.category,
        technologies: techStackItem.technologies.join(', '),
        image_url: techStackItem.image_url || '',
        display_order: techStackItem.display_order,
        is_active: techStackItem.is_active
      })
    }
  }, [techStackItem])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const techData = {
        category: formData.category,
        technologies: formData.technologies.split(',').map(tech => tech.trim()).filter(tech => tech),
        image_url: formData.image_url || null,
        display_order: formData.display_order,
        is_active: formData.is_active
      }

      if (techStackItem) {
        const { error } = await supabase
          .from('tech_stack')
          .update(techData)
          .eq('id', techStackItem.id)
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Tech stack updated successfully",
        })
      } else {
        const { error } = await supabase
          .from('tech_stack')
          .insert([techData])
        
        if (error) throw error
        
        toast({
          title: "Success",
          description: "Tech stack created successfully",
        })
      }
      
      onSave()
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${techStackItem ? 'update' : 'create'} tech stack`,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{techStackItem ? 'Edit Tech Stack' : 'Add New Tech Stack'}</CardTitle>
        <CardDescription>
          {techStackItem ? 'Update tech stack information' : 'Create a new tech stack category'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="technologies">Technologies (comma separated)</Label>
            <Input
              id="technologies"
              value={formData.technologies}
              onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
              placeholder="React, Vue.js, Angular"
              required
            />
          </div>

          <ImageUpload
            currentImageUrl={formData.image_url}
            onImageUpload={(url) => setFormData({ ...formData, image_url: url })}
            onImageRemove={() => setFormData({ ...formData, image_url: '' })}
            label="Tech Stack Image"
            folder="tech-stack"
          />
          
          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>
          
          <div className="flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : (techStackItem ? 'Update Tech Stack' : 'Create Tech Stack')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default TechStackForm
